import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { isOwnerOrFinance } from "../../utils/role-check";
import { useProjectRole } from "../../hooks/useProjectRole";
import { useCustomerList } from "../../hooks/useCustomerList";
import { useContactPersons } from "../../hooks/useContactPersons";
import BackButton from "../Navigation/BackButton";
import AIDocumentUpload from "../Documents/AIDocumentUpload";
import CustomerAccessManager from "../CustomerAccess/CustomerAccessManager";
// import "./projects.style.css";
import "./project-detail-modern.css";
import StyledModalView from "../Modal/StyledModalView";
import DonutChart from "../Charts/DonutChart";
import GoogleMapDark from "../Map/GoogleMapDark";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import PdfViewer from "../PdfViewer/PdfViewer";

function ProjectDetailView(props) {
  const { t } = useTranslation();
  const {
    project,
    editableProject,
    profit,
    isEditing,
    loading,
    error,
    employees,
    availableEmployees,
    documents,
    auth,
    showAddEmployee,
    setShowAddEmployee,
    showUploadForm,
    setShowUploadForm,
    setSelectedEmployee,
    setSelectedRole,
    rateType,
    setRateType,
    hourlyRate,
    setHourlyRate,
    meterRate,
    setMeterRate,
    editingEmployeeId,
    setEditingEmployeeId,
    newHourlyRate,
    setNewHourlyRate,
    newMeterRate,
    setNewMeterRate,
    newProjectRole,
    setNewProjectRole,
    newRateType,
    setNewRateType,
    setFile,
    documentCategory,
    setDocumentCategory,
    expiresAt,
    setExpiresAt,
    handleInputChange,
    handleEditClick,
    handleCancelClick,
    handleSaveClick,
    handleAddEmployee,
    handleEditEmployee,
    handleSaveEmployeeChanges,
    handleDocumentUpload,
    handleDeleteDocument,
    totalEmployeeCosts,
    totalExtraMaterialCosts,
    totalHours,
    totalMeters,
    requestRemoveEmployee,
    confirmRemoveEmployee,
    cancelRemoveEmployee,
    showRemoveEmployeeModal,
    employeeToRemove,
    editingDocId,
    editedExpiry,
    setEditedExpiry,
    handleEditExpiry,
    handleSaveExpiry,
    handleCancelEditExpiry
  } = props;

  // Get baseUrl and id for document links
  const baseUrl = (window.env && window.env.REACT_APP_BASE_URL) || process.env.REACT_APP_BASE_URL || "http://localhost:8080";
  const id = project?.id;
  const selectedUserId = auth?.userId; // fallback to logged-in user if not provided
  const { projectRole: selectedUserProjectRole, loading: roleLoading } = useProjectRole(id, selectedUserId);

  // PieChart interactivity state
  const [activeIndex, setActiveIndex] = useState(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedPdfName, setSelectedPdfName] = useState("");

  // Contact person selection for edit mode and legacy contact lookup for view mode
  const { customers } = useCustomerList();
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Find customer based on client name (from either project or editableProject)
  useEffect(() => {
    const clientName = editableProject?.client || project?.client;
    if (clientName && customers) {
      const customer = customers.find(c => c.name === clientName);
      setSelectedCustomerId(customer?.id || null);
      setSelectedCustomer(customer || null);
    } else {
      setSelectedCustomerId(null);
      setSelectedCustomer(null);
    }
  }, [editableProject?.client, project?.client, customers]);

  const { contactPersons } = useContactPersons(selectedCustomerId);

  if (loading) return <Loader message={t('common.loading')} />;
  if (error) return <NoDataConstruction text={t('common.error')}/>;
  if (!project) return <NoDataConstruction text={t('project.noProjectFound')}/>;
  if (!auth) return <Loader message={t('common.loading')} />;
  if (roleLoading) return <Loader message={t('common.loading')} />;
  console.log("employees:", employees);
  return (
    <div className="project-detail-modern">
      <BackButton label={t('project.backToProjects')} />

      {/* Modern Header with Status and Actions */}
      <div className="project-header">
        {isEditing && isOwnerOrFinance(auth.role) ? (
          <div className="project-name-edit">
            <input
              type="text"
              name="name"
              value={editableProject.name}
              onChange={handleInputChange}
              className="project-name-input"
            />
          </div>
        ) : (
          <h2>
            {project.name}
            {project.status && project.status !== 'active' && (
              <span className={`project-status-badge archived`}>
                {t('projects.archived') || 'Gearchiveerd'}
              </span>
            )}
          </h2>
        )}

        {isOwnerOrFinance(auth.role) && (
          <div className="project-header-actions">
            {!isEditing ? (
              <button type="button" className="btn btn-edit" onClick={handleEditClick}>
                {t('common.edit')}
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-accept" onClick={handleSaveClick}>
                  {t('common.save')}
                </button>
                <button type="button" className="btn btn-cancel" onClick={handleCancelClick}>
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={`${!isOwnerOrFinance(auth.role) ? "full-width" : ""}`}>
        {isEditing && isOwnerOrFinance(auth.role) ? (
          <div className="project-info-card">
            <div className="edit-form-grid">
              <div className="form-group-modern">
                <label>{t('projects.client')}:</label>
                <input type="text" name="client" value={editableProject.client || ''}
                       onChange={handleInputChange}/>
              </div>

                {/* Contact Person Selector - shown when editing */}
                {selectedCustomerId && (
                  <div className="form-group-modern">
                    <label>{t('customers.contactPerson')}:</label>
                    {contactPersons && contactPersons.length > 0 ? (
                      <select
                        name="contact_person_id"
                        value={editableProject.contact_person_id || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">{t('customers.noContactPersonSelected')}</option>
                        {contactPersons.map((cp) => (
                          <option key={cp.id} value={cp.id}>
                            {cp.name} {cp.is_primary ? '(Primary)' : ''} {cp.position ? `- ${cp.position}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : selectedCustomer?.contact_person ? (
                      <input
                        type="text"
                        value={selectedCustomer.contact_person}
                        disabled
                        className="input-disabled"
                        title={t('customers.mainContactPersonLegacy')}
                      />
                    ) : (
                      <input
                        type="text"
                        value={t('customers.noContactPersons')}
                        disabled
                        className="input-disabled input-muted"
                      />
                    )}
                  </div>
                )}

                <div className="form-group-modern">
                  <label>{t('projects.city')}:</label>
                  <input type="text" name="location" value={editableProject.location || ''}
                         onChange={handleInputChange}/>
                </div>

                <div className="form-group-modern">
                  <label>{t('projects.address')}:</label>
                  <input type="text" name="address" value={editableProject.address || ''}
                         onChange={handleInputChange}/>
                </div>

                <div className="form-group-modern">
                  <label>{t('projects.startDate')}:</label>
                  <input type="date" name="start_date"
                         value={editableProject.start_date ? editableProject.start_date.split('T')[0] : ''}
                         onChange={handleInputChange}/>
                </div>

                <div className="form-group-modern">
                  <label>{t('projects.endDate')}:</label>
                  <input type="date" name="end_date"
                         value={editableProject.end_date ? editableProject.end_date.split('T')[0] : ''}
                         onChange={handleInputChange}/>
                </div>

                <div className="form-group-modern">
                  <label>{t('projects.whatsappProjectGroup')}:</label>
                  <input type="text" name="whatsapp" value={editableProject.whatsapp || ''}
                         onChange={handleInputChange}/>
                </div>
            </div>
          </div>
        ) : (
              <div className="project-detail-grid">
                {/* Basic Information Card */}
                <div className="project-info-card">
                  <h3>{t('projects.projectInfo') || 'Project Informatie'}</h3>
                  <div className="info-row">
                    <span className="info-label">{t('projects.projectNumber')}</span>
                    <span className="info-value">{project.id}-{project.start_date ? new Date(project.start_date).getFullYear() : 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('projects.client')}</span>
                    <span className="info-value">{project.client}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('projects.city')}</span>
                    <span className="info-value">{project.location}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('projects.address')}</span>
                    <span className="info-value">{project.address}</span>
                  </div>
                </div>

                {/* Project Timeline Card */}
                <div className="project-info-card">
                  <h3>{t('projects.timeline') || 'Planning'}</h3>
                  {project.start_date && (
                    <div className="info-row">
                      <span className="info-label">{t('projects.startDate')}</span>
                      <span className="info-value">{new Date(project.start_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {project.end_date && (
                    <div className="info-row">
                      <span className="info-label">{t('projects.endDate')}</span>
                      <span className="info-value">{new Date(project.end_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {project.whatsapp && (
                    <div className="info-row">
                      <span className="info-label">WhatsApp</span>
                      <a
                        href={project.whatsapp.startsWith('http') ? project.whatsapp : `https://wa.me/${project.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-button-modern"
                      >
                        {t('projects.joinWhatsappGroup') || 'Join WhatsApp Group'}
                      </a>
                    </div>
                  )}
                </div>

                {/* Contact Person Card */}
                {project.contact_person ? (
                  <div className="contact-person-card">
                    <h4>{t('customers.contactPerson')}</h4>
                    <div className="contact-person-name">{project.contact_person.name}</div>
                    {project.contact_person.position && (
                      <div className="contact-person-position">{project.contact_person.position}</div>
                    )}
                    <div className="contact-person-details">
                      {project.contact_person.email && (
                        <div className="contact-detail-item">
                          <a href={`mailto:${project.contact_person.email}`}>{project.contact_person.email}</a>
                        </div>
                      )}
                      {project.contact_person.phone && (
                        <div className="contact-detail-item">
                          <a href={`tel:${project.contact_person.phone}`}>{project.contact_person.phone}</a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : selectedCustomer?.contact_person ? (
                  <div className="contact-person-card">
                    <h4>{t('customers.contactPerson')}</h4>
                    <div className="contact-person-name">{selectedCustomer.contact_person}</div>
                    <div className="contact-person-legacy-note">
                      <small>{t('customers.mainContactPersonLegacy')}</small>
                    </div>
                  </div>
                ) : (
                  <div className="project-info-card">
                    <h3>{t('customers.contactPerson')}</h3>
                    <div className="no-contact-person">
                      {t('customers.noContactPersonSelected')}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isOwnerOrFinance(auth.role) && (
              <div className="project-info-card">
                <h3>{t('projects.finances')}</h3>
                {isEditing ? (
                  <div className="edit-form-grid">
                    <div className="form-group-modern">
                      <label>{t('projects.quotationPrice')} (€):</label>
                      <input type="number" step="0.01" name="projectprice"
                             value={editableProject.projectprice || ''}
                             onChange={handleInputChange}/>
                    </div>
                    <div className="form-group-modern">
                      <label>{t('projects.projectHourlyRate')} (€ {t('projects.perHour')}):</label>
                      <input type="number" step="0.01" name="projectrate"
                             value={editableProject.projectrate || ''}
                             onChange={handleInputChange}/>
                    </div>
                    <div className="form-group-modern">
                      <label>{t('projects.materialCosts')} (€):</label>
                      <input type="number" step="0.01" name="materialcosts"
                             value={editableProject.materialcosts || ''}
                             onChange={handleInputChange}/>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="info-row">
                      <span className="info-label">{t('projects.quotationPrice')}</span>
                      <span className="info-value">€ {project.projectprice.toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t('projects.projectHourlyRate')}</span>
                      <span className="info-value">€ {project.projectrate.toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} {t('projects.perHour')}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t('projects.materialCosts')}</span>
                      <span className="info-value">€ {project.materialcosts.toLocaleString("nl-NL", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        {/* Projectvoortgang overzicht */}
        {isOwnerOrFinance(auth.role) && (
          <div className="financial-overview-modern">
            <h3>{t('projects.financialOverview') || 'Financieel Overzicht'}</h3>
            <div className="financial-grid">
              <div className="financial-stat">
                <div className="financial-stat-label">{profit >= 0 ? t('projects.profit') || "Winst" : t('projects.loss') || "Verlies"}</div>
                <div className={`financial-stat-value ${profit >= 0 ? "profit" : "loss"}`}>
                  € {profit !== null ? profit.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "..."}
                </div>
              </div>
              <div className="financial-stat">
                <div className="financial-stat-label">{t('projects.laborCosts') || "Loonkosten"}</div>
                <div className="financial-stat-value">
                  € {typeof totalEmployeeCosts === "number" ? totalEmployeeCosts.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "..."}
                </div>
              </div>
              <div className="financial-stat">
                <div className="financial-stat-label">{t('projects.materialCosts') || "Materiaalkosten"}</div>
                <div className="financial-stat-value">
                  € {typeof project.materialcosts === "number" ? project.materialcosts.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "..."}
                </div>
              </div>
              <div className="financial-stat">
                <div className="financial-stat-label">{t('projects.extraMaterialCosts') || "Extra Materiaalkosten"}</div>
                <div className="financial-stat-value">
                  € {typeof totalExtraMaterialCosts === "number" ? totalExtraMaterialCosts.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "..."}
                </div>
              </div>
              <div className="financial-stat">
                <div className="financial-stat-label">{t('projects.totalHours') || "Geregistreerde Uren"}</div>
                <div className="financial-stat-value">
                  {typeof totalHours === "number" ? totalHours.toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "..."} {t('projects.hours') || "Uren"}
                </div>
              </div>
              <div className="financial-stat">
                <div className="financial-stat-label">{t('projects.totalMeters') || "Geregistreerde Meters"}</div>
                <div className="financial-stat-value">
                  {typeof totalMeters === "number" ? totalMeters.toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "..."} m
                </div>
              </div>
            </div>
            {/* Pie Chart for visual representation */}
            <div className="totals-piechart-wrapper">
              <DonutChart
                data={[
                  {
                    name: t('projects.laborCosts') || 'Loonkosten',
                    value: typeof totalEmployeeCosts === 'number' ? Math.abs(totalEmployeeCosts) : 0,
                    color: '#7393b0'
                  },
                  {
                    name: t('projects.materialCosts') || 'Materiaalkosten',
                    value: typeof project.materialcosts === 'number' ? Math.abs(project.materialcosts) : 0,
                    color: '#565a5e'
                  },
                  {
                    name: t('projects.extraMaterialCosts') || 'Extra materiaalkosten',
                    value: typeof totalExtraMaterialCosts === 'number' ? Math.abs(totalExtraMaterialCosts) : 0,
                    color: '#cdcdce'
                  },
                  ...(profit > 0 ? [{
                    name: t('projects.profit') || 'Winst',
                    value: profit,
                    color: '#4ade80'
                  }] : [])
                ].filter(item => item.value > 0)}
                showLegend={true}
              />
            </div>
          </div>
        )}
        {/* Werknemers overzicht */}
        <div className="employees-section">
          <h2>{t('projects.projectEmployees')}</h2>

          {isOwnerOrFinance(auth.role) && (
            <>
              {!showAddEmployee ? (
                <button
                  type="button"
                  className="btn btn-add"
                  onClick={() => setShowAddEmployee(true)}
                >
                  {t('common.add')} {t('projects.employee')}
                </button>
              ) : (
                <div className="add-employee-card">
                  <h3>{t('common.add')} {t('projects.employee')}</h3>
                  <div className="employee-form-grid">
                    <div className="form-group-modern">
                      <label>{t('projects.employee')}</label>
                      <select onChange={(e) => setSelectedEmployee(e.target.value)}>
                        <option value="">{t('projects.selectEmployee')}</option>
                        {availableEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>{emp.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group-modern">
                      <label>{t('projects.role')}</label>
                      <select onChange={(e) => setSelectedRole(e.target.value)}>
                        <option value="">{t('projects.role')}</option>
                        <option value="zzper">{t('user.roles.zzper')}</option>
                        <option value="voorman">{t('user.roles.voorman')}</option>
                      </select>
                    </div>

                    <div className="form-group-modern">
                      <label>{t('timesheet.rateType')}</label>
                      <select onChange={(e) => setRateType(e.target.value)} value={rateType}>
                        <option value="hourly">{t('timesheet.hourlyRate')}</option>
                        <option value="meter">{t('timesheet.meterRate')}</option>
                      </select>
                    </div>

                    <div className="form-group-modern">
                      <label>{t('timesheet.rate')}</label>
                      {rateType === "hourly" ? (
                        <input
                          type="number"
                          placeholder={`${t('timesheet.hourlyRate')} (€)`}
                          value={hourlyRate}
                          onChange={(e) => setHourlyRate(e.target.value)}
                        />
                      ) : (
                        <input
                          type="number"
                          placeholder={`${t('timesheet.meterRate')} (€)`}
                          value={meterRate}
                          onChange={(e) => setMeterRate(e.target.value)}
                        />
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-accept"
                        onClick={handleAddEmployee}
                      >
                        {t('common.submit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => setShowAddEmployee(false)}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {employees.length > 0 ? (
            <div className="employees-grid">
              {employees.map((employee) => (
                <div key={employee.user_id} className="employee-card">
                  <div className="employee-card-header">
                    {employee.profile_picture && (
                      <img
                        src={employee.profile_picture}
                        alt={employee.user_name}
                        className="employee-avatar"
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginRight: '12px'
                        }}
                      />
                    )}
                    {!employee.profile_picture && (
                      <div
                        className="employee-avatar-placeholder"
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px',
                          fontSize: '20px',
                          fontWeight: '600',
                          color: '#6b7280'
                        }}
                      >
                        {employee.user_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div className="employee-name">{employee.user_name}</div>
                      <div className="employee-role">
                        {editingEmployeeId === employee.user_id ? (
                          <select
                            value={newProjectRole}
                            onChange={(e) => setNewProjectRole(e.target.value)}
                            className="employee-role-select"
                          >
                            <option value="zzper">{t('user.roles.zzper')}</option>
                            <option value="voorman">{t('user.roles.voorman')}</option>
                          </select>
                        ) : (
                          t(`user.roles.${employee.project_role}`)
                        )}
                      </div>
                    </div>
                  </div>

                  {isOwnerOrFinance(auth.role) && (
                    <div className="employee-details">
                      <div className="employee-detail-row">
                        <span className="employee-detail-label">{t('timesheet.rateType')}</span>
                        <span className="employee-detail-value">
                          {editingEmployeeId === employee.user_id ? (
                            <select
                              value={newRateType}
                              onChange={(e) => setNewRateType(e.target.value)}
                              className="employee-rate-select"
                            >
                              <option value="hourly">{t('timesheet.hourlyRate')}</option>
                              <option value="meter">{t('timesheet.meterRate')}</option>
                            </select>
                          ) : (
                            employee.rate_type === "meter" ? t('timesheet.meterRate') : t('timesheet.hourlyRate')
                          )}
                        </span>
                      </div>

                      <div className="employee-detail-row">
                        <span className="employee-detail-label">{t('timesheet.rate')}</span>
                        <span className="employee-detail-value">
                          {editingEmployeeId === employee.user_id ? (
                            <input
                              type="number"
                              value={newRateType === "meter" ? newMeterRate : newHourlyRate}
                              onChange={(e) => newRateType === "meter" ? setNewMeterRate(e.target.value) : setNewHourlyRate(e.target.value)}
                              className="employee-rate-input"
                            />
                          ) : (
                            `€ ${employee.rate_type === "meter" ? 
                              employee.meter_rate.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 
                              employee.hourly_rate.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            }`
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {isOwnerOrFinance(auth.role) && (
                    <div className="employee-actions-card">
                      {editingEmployeeId === employee.user_id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-accept"
                            onClick={() => handleSaveEmployeeChanges(
                              employee.user_id,
                              newRateType,
                              newMeterRate,
                              newHourlyRate,
                              newProjectRole
                            )}
                          >
                            {t('common.save')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={() => setEditingEmployeeId(null)}
                          >
                            {t('common.cancel')}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-edit"
                            onClick={() => handleEditEmployee(employee)}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-delete"
                            onClick={() => requestRemoveEmployee(employee.user_id)}
                          >
                            {t('common.delete')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-employees-message">
              {t('projects.noEmployeesAssigned') || 'Geen medewerkers gekoppeld'}
            </div>
          )}
        </div>
        {/* Project Documenten */}
        <div className="documents-section">
          <h2>{t('projects.projectDocuments')}</h2>

          {isOwnerOrFinance(auth.role) && (
            <>
              {!showUploadForm ? (
                <button
                  type="button"
                  onClick={() => setShowUploadForm(true)}
                  className="btn btn-add"
                >
                  {t('common.upload')} {t('projects.projectDocuments')}
                </button>
              ) : (
                <div className="upload-document-card">
                  <div className="upload-document-header">
                    <h3>
                      {t('common.upload')} {t('documents.document')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowUploadForm(false)}
                      className="btn btn-cancel"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                  <AIDocumentUpload
                    projectId={project?.id}
                    expectedDocumentType={null}
                    onValidationComplete={(result) => {
                      console.log('AI Validation:', result);
                    }}
                    onUploadSuccess={(data) => {
                      console.log('Upload success:', data);
                      setShowUploadForm(false);
                      window.location.reload();
                    }}
                  />
                </div>
              )}
            </>
          )}

          {documents.length > 0 ? (
            <div className="documents-grid">
              {documents.map(doc => {
                const isExpired = doc.expires_at && new Date(doc.expires_at) < new Date();
                return (
                  <div key={doc.id} className={`document-card ${isExpired ? 'expired' : ''}`}>
                    <div className="document-header">
                      <div className="document-info">
                        <div className="document-name" title={doc.file_name}>
                          {doc.file_name}
                        </div>
                        <div className="document-type">
                          {doc.document_category.charAt(0).toUpperCase() + doc.document_category.slice(1)}
                        </div>
                      </div>
                    </div>

                    <div className="document-expiry">
                      <span className="document-expiry-label">
                        {t('documents.expiresAt') || 'Verloopt op'}:
                      </span>
                      {editingDocId === doc.id ? (
                        <input
                          type="date"
                          value={editedExpiry}
                          onChange={(e) => setEditedExpiry(e.target.value)}
                        />
                      ) : (
                        <span className={`document-expiry-value ${isExpired ? 'expired' : ''}`}>
                          {doc.expires_at ? new Date(doc.expires_at).toLocaleDateString('nl-NL') : t('documents.notSet') || 'Niet ingesteld'}
                        </span>
                      )}
                    </div>

                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn btn-view"
                        onClick={() => {
                          const pdfUrl = `${baseUrl}/uploads/projects/${id}/${encodeURIComponent(doc.file_name)}`;
                          setSelectedPdf(pdfUrl);
                          setSelectedPdfName(doc.file_name);
                          setPdfViewerOpen(true);
                        }}
                      >
                        {t('common.view')}
                      </button>

                      {isOwnerOrFinance(auth.role) && (
                        <>
                          {editingDocId === doc.id ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-accept"
                                onClick={() => handleSaveExpiry(doc.id)}
                              >
                                {t('common.save')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={handleCancelEditExpiry}
                              >
                                {t('common.cancel')}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-edit"
                                onClick={() => handleEditExpiry(doc)}
                              >
                                {t('common.edit')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-delete"
                                onClick={() => handleDeleteDocument(doc.id)}
                                title={t('common.delete')}
                              >
                                {t('common.delete')}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-documents-message">
              {t('projects.noDocumentsAvailable')}
            </div>
          )}
        </div>

        {/* Customer Access Management - Only for Admin */}
        {isOwnerOrFinance(auth.role) && (
          <CustomerAccessManager projectId={project.id} />
        )}

        {/* Project Locatie */}
        <h3>{t('projects.projectLocation')}</h3>
        <div className="map-container">
          <GoogleMapDark
            address={`${project.address || ''}, ${project.location || ''}`}
            lat={project.latitude}
            lng={project.longitude}
          />
        </div>
        <StyledModalView
          isOpen={showRemoveEmployeeModal}
          onClose={cancelRemoveEmployee}
          title={t('common.delete')}
          closeText={t('common.cancel')}
          confirmText={t('common.delete')}
          onConfirm={() => confirmRemoveEmployee(props.fetchProjectDetails, props.showFlash)}
        >
          {t('common.confirm')}
        </StyledModalView>
        {/* PDF Viewer for inline viewing */}
        {pdfViewerOpen && selectedPdf && (
          <PdfViewer
            pdfUrl={selectedPdf}
            fileName={selectedPdfName}
            onClose={() => {
              setPdfViewerOpen(false);
              setSelectedPdf(null);
              setSelectedPdfName("");
            }}
          />
        )}
      </div>
  );
}

ProjectDetailView.propTypes = {
    project: PropTypes.object,
    editableProject: PropTypes.object,
    profit: PropTypes.number,
    isEditing: PropTypes.bool,
    loading: PropTypes.bool,
    error: PropTypes.string,
    employees: PropTypes.array,
    availableEmployees: PropTypes.array,
    documents: PropTypes.array,
    auth: PropTypes.object,
    showAddEmployee: PropTypes.bool,
    setShowAddEmployee: PropTypes.func,
    showUploadForm: PropTypes.bool,
    setShowUploadForm: PropTypes.func,
    setSelectedEmployee: PropTypes.func,
    setSelectedRole: PropTypes.func,
    rateType: PropTypes.string,
    setRateType: PropTypes.func,
    hourlyRate: PropTypes.number,
    setHourlyRate: PropTypes.func,
    meterRate: PropTypes.number,
    setMeterRate: PropTypes.func,
    editingEmployeeId: PropTypes.number,
    setEditingEmployeeId: PropTypes.func,
    newHourlyRate: PropTypes.number,
    setNewHourlyRate: PropTypes.func,
    newMeterRate: PropTypes.number,
    setNewMeterRate: PropTypes.func,
    newProjectRole: PropTypes.string,
    setNewProjectRole: PropTypes.func,
    newRateType: PropTypes.string,
    setNewRateType: PropTypes.func,
    setFile: PropTypes.func,
    documentCategory: PropTypes.string,
    setDocumentCategory: PropTypes.func,
    expiresAt: PropTypes.string,
    setExpiresAt: PropTypes.func,
    handleInputChange: PropTypes.func,
    handleEditClick: PropTypes.func,
    handleCancelClick: PropTypes.func,
    handleSaveClick: PropTypes.func,
    handleAddEmployee: PropTypes.func,
    handleEditEmployee: PropTypes.func,
    handleSaveEmployeeChanges: PropTypes.func,
    handleDocumentUpload: PropTypes.func,
    handleDeleteDocument: PropTypes.func,
    totalEmployeeCosts: PropTypes.number,
    totalExtraMaterialCosts: PropTypes.number,
    totalHours: PropTypes.number,
    totalMeters: PropTypes.number,
    requestRemoveEmployee: PropTypes.func,
    confirmRemoveEmployee: PropTypes.func,
    cancelRemoveEmployee: PropTypes.func,
    showRemoveEmployeeModal: PropTypes.bool,
    employeeToRemove: PropTypes.object,
    editingDocId: PropTypes.number,
    editedExpiry: PropTypes.string,
    setEditedExpiry: PropTypes.func,
    handleEditExpiry: PropTypes.func,
    handleSaveExpiry: PropTypes.func,
    handleCancelEditExpiry: PropTypes.func,
};

export default ProjectDetailView;
